/**
 * QR Code Display Component
 *
 * Renders a QR code with CrowdTalkie styling and optional download functionality.
 */

import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useRef } from 'react'

interface QRCodeProps {
  value: string
  size?: number
  showDownload?: boolean
  downloadFileName?: string
  className?: string
}

export function QRCode({
  value,
  size = 200,
  showDownload = false,
  downloadFileName = 'crowdtalkie-room-qr',
  className,
}: QRCodeProps) {
  const svgRef = useRef<HTMLDivElement>(null)

  const handleDownload = useCallback(() => {
    if (!svgRef.current) return

    const svg = svgRef.current.querySelector('svg')
    if (!svg) return

    // Create canvas with padding for white border
    const padding = 16
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = size + padding * 2
    canvas.height = size + padding * 2

    // White background with padding
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Convert SVG to data URL
    const svgData = new XMLSerializer().serializeToString(svg)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const svgUrl = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, padding, padding, size, size)
      URL.revokeObjectURL(svgUrl)

      // Download
      const link = document.createElement('a')
      link.download = `${downloadFileName}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    }
    img.src = svgUrl
  }, [size, downloadFileName])

  return (
    <div className={className}>
      {/* QR Code with white background in tactical frame */}
      <div className="relative p-2 glass-panel border border-white/20 inline-block">
        <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-white/50 rounded-tl-sm"></div>
        <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-white/50 rounded-tr-sm"></div>
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-white/50 rounded-bl-sm"></div>
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-white/50 rounded-br-sm"></div>

        <div
          ref={svgRef}
          className="bg-white p-4 rounded-lg"
          style={{ lineHeight: 0 }}
        >
          <QRCodeSVG
            value={value}
            size={size}
            level="M"
            bgColor="#ffffff"
            fgColor="#181111"
          />
        </div>
      </div>

      {/* Download button */}
      {showDownload && (
        <button
          onClick={handleDownload}
          className="mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded bg-white/5 text-white/80 hover:bg-white/10 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest w-full border border-white/10"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Save Image
        </button>
      )}
    </div>
  )
}
